/**
 * Match info rows: date/time, league + flag, referee, venue — no calendar or TV row.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { CalendarDays, Flag, User } from 'lucide-react-native';
import { SectionCard } from '../SectionCard';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { formatTimeOnly } from '../../../helpers/formatDateTime.helper';
import { formatDateLine } from './matchHubMatchInfo.helper';
import { getMatchHubMatchInfoCardStyles } from './MatchHubMatchInfoCard.styles';

export interface MatchHubMatchInfoCardProps {
  data: MatchHubMockData;
}

export const MatchHubMatchInfoCard: React.FC<MatchHubMatchInfoCardProps> = ({ data }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubMatchInfoCardStyles(theme), [theme]);
  const dateLine = formatDateLine(data.kickoffIso);
  const timeLine = formatTimeOnly(data.kickoffIso);
  const { matchInfo, meta } = data;
  const hasReferee = typeof meta.referee === 'string' && meta.referee.trim().length > 0;
  const venueCapacity = matchInfo.venueCapacity ?? 0;
  const hasCapacity = venueCapacity > 0;
  const cap = hasCapacity ? venueCapacity.toLocaleString('en-GB') : '';

  const iconColor = theme.primary;

  return (
    <SectionCard cardStyle={styles.sectionCardDense} testID="match-hub-match-info">
      <View style={styles.row}>
        <View style={styles.iconWrap}>
          <CalendarDays size={18} color={iconColor} strokeWidth={2} />
        </View>
        <View style={styles.textCol}>
          {dateLine !== '' && <Text style={styles.primary}>{dateLine}</Text>}
          {timeLine !== '' && (
            <Text style={styles.secondary}>{timeLine}</Text>
          )}
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.flagWrap}>
          <CountryFlag
            countryCode={matchInfo.countryCode}
            size="mini"
            accessibilityLabel={`${matchInfo.leagueWithCountry} country flag`}
          />
        </View>
        <View style={styles.textCol}>
          <Text style={styles.primary}>{matchInfo.roundDisplay}</Text>
          <Text style={styles.secondary}>{matchInfo.leagueWithCountry}</Text>
        </View>
      </View>

      {hasReferee && (
        <View style={styles.row}>
          <View style={styles.iconWrap}>
            <User size={18} color={iconColor} strokeWidth={2} />
          </View>
          <View style={styles.textCol}>
            <Text style={styles.primary}>{meta.referee}</Text>
            <Text style={styles.secondary}>Referee</Text>
          </View>
        </View>
      )}

      <View style={[styles.row, styles.rowLast]}>
        <View style={styles.iconWrap}>
          <Flag size={18} color={iconColor} strokeWidth={2} />
        </View>
        <View style={styles.textCol}>
          <Text style={styles.primary}>{meta.venue}</Text>
          {hasCapacity && <Text style={styles.secondary}>Capacity: {cap}</Text>}
        </View>
      </View>
    </SectionCard>
  );
};
